export const AFFECT_LABELS = [
  "anger",
  "disgust",
  "fear",
  "happy",
  "neutral",
  "sad",
  "surprise",
];

const API7_ALIASES = {
  anger: "anger",
  angry: "anger",
  disgust: "disgust",
  fear: "fear",
  anxious: "fear",
  anxiety: "fear",
  happiness: "happy",
  happy: "happy",
  joy: "happy",
  neutral: "neutral",
  calm: "neutral",
  sadness: "sad",
  sad: "sad",
  sorrow: "sad",
  surprise: "surprise",
  surprised: "surprise",
};

const BASE_WEIGHTS = {
  voice: 0.35,
  face: 0.2,
};

const CONFIRMATION_REQUIRED_LABELS = new Set([
  "anger",
  "disgust",
  "fear",
  "sad",
]);

const ADAPTATION_POLICIES = {
  anger: {
    title: "Calm repair",
    instruction: "calm tone, concise wording, acknowledge friction, offer concrete options",
  },
  disgust: {
    title: "Trust repair",
    instruction: "acknowledge mismatch, avoid defensiveness, move to a cleaner alternative",
  },
  fear: {
    title: "Reduce uncertainty",
    instruction: "explain the next step, reduce uncertainty, avoid excessive enthusiasm",
  },
  happy: {
    title: "Maintain momentum",
    instruction: "slightly higher energy, keep momentum, continue naturally",
  },
  sad: {
    title: "Warm support",
    instruction: "warmer tone, shorter response, slower pace, ask at most one question",
  },
  surprise: {
    title: "Orient quickly",
    instruction: "briefly orient the user, clarify what changed, keep the response grounded",
  },
  neutral: {
    title: "Default persona",
    instruction: "default persona, no emotion-specific adjustment",
  },
  uncertain: {
    title: "Hold default",
    instruction: "default persona, do not adapt from low-confidence signals",
  },
};

const VALENCE = {
  anger: -0.65,
  disgust: -0.55,
  fear: -0.7,
  happy: 0.78,
  neutral: 0,
  sad: -0.72,
  surprise: 0.1,
  uncertain: 0,
};

const AROUSAL = {
  anger: 0.78,
  disgust: 0.46,
  fear: 0.68,
  happy: 0.48,
  neutral: 0,
  sad: -0.42,
  surprise: 0.82,
  uncertain: 0,
};

function clamp01(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function canonicalLabel(label) {
  if (!label) return null;
  return API7_ALIASES[String(label).trim().toLowerCase()] || null;
}

export function normalizeScores(scores) {
  const normalized = Object.fromEntries(AFFECT_LABELS.map((label) => [label, 0]));
  if (!scores || typeof scores !== "object") return normalized;

  for (const [rawLabel, rawValue] of Object.entries(scores)) {
    const label = canonicalLabel(rawLabel);
    if (!label) continue;
    normalized[label] += clamp01(Number(rawValue));
  }

  const total = Object.values(normalized).reduce((sum, value) => sum + value, 0);
  if (total <= 0) return normalized;
  return Object.fromEntries(
    AFFECT_LABELS.map((label) => [label, normalized[label] / total]),
  );
}

export function topScore(scores) {
  return AFFECT_LABELS
    .map((label) => ({ label, value: clamp01(scores?.[label]) }))
    .sort((left, right) => right.value - left.value)[0] || {
      label: "uncertain",
      value: 0,
    };
}

export function scoreConfidence(scores, fallbackLabel) {
  const top = topScore(scores);
  if (top.value > 0) return top.value;
  const label = canonicalLabel(fallbackLabel);
  return label ? 0.45 : 0;
}

export function observationFromScores(source, scores, extras = {}) {
  const normalized = normalizeScores(scores);
  const top = topScore(normalized);
  return {
    source,
    scores: normalized,
    label: canonicalLabel(extras.label) || top.label,
    confidence: clamp01(extras.confidence ?? top.value),
    quality: clamp01(extras.quality ?? 1),
    observedAt: extras.observedAt || Date.now(),
    detail: extras.detail || "",
  };
}

export function aggregateObservations(observations) {
  const valid = observations.filter(Boolean);
  if (!valid.length) return null;

  const scores = {};
  for (const label of AFFECT_LABELS) {
    const values = valid
      .map((observation) => clamp01(observation.scores?.[label]))
      .sort((left, right) => left - right);
    const mid = Math.floor(values.length / 2);
    scores[label] = values.length % 2
      ? values[mid]
      : (values[mid - 1] + values[mid]) / 2;
  }

  const confidence = valid.reduce((sum, observation) => (
    sum + clamp01(observation.confidence)
  ), 0) / valid.length;

  return observationFromScores(valid[0].source, scores, {
    confidence,
    quality: valid.reduce((sum, observation) => (
      sum + clamp01(observation.quality)
    ), 0) / valid.length,
    observedAt: Math.max(...valid.map((observation) => observation.observedAt || 0)),
  });
}

function freshness(observedAt, now) {
  if (!observedAt) return 0.2;
  const ageSeconds = Math.max(0, (now - observedAt) / 1000);
  if (ageSeconds <= 6) return 1;
  if (ageSeconds >= 24) return 0;
  return 1 - ((ageSeconds - 6) / 18);
}

function effectiveWeight(source, observation, now) {
  if (!observation) return 0;
  return (BASE_WEIGHTS[source] || 0)
    * clamp01(observation.confidence)
    * clamp01(observation.quality ?? 1)
    * freshness(observation.observedAt, now);
}

function smoothScores(nextScores, previousScores, alpha = 0.42) {
  if (!previousScores) return nextScores;
  return Object.fromEntries(
    AFFECT_LABELS.map((label) => [
      label,
      (nextScores[label] * alpha) + (clamp01(previousScores[label]) * (1 - alpha)),
    ]),
  );
}

function valenceArousal(scores) {
  return AFFECT_LABELS.reduce(
    (acc, label) => ({
      valence: acc.valence + scores[label] * VALENCE[label],
      arousal: acc.arousal + scores[label] * AROUSAL[label],
    }),
    { valence: 0, arousal: 0 },
  );
}

function confidenceLabel(top) {
  if (top.value < 0.55) return "uncertain";
  return top.label;
}

function conflictList(face, voice) {
  if (!face || !voice) return [];
  if (face.confidence < 0.45 || voice.confidence < 0.45) return [];
  if (face.label === voice.label) return [];
  return [`face_${face.label}_vs_voice_${voice.label}`];
}

export function fuseAffect({ face, voice, previous, now = Date.now() }) {
  const sources = { face, voice };
  const weights = Object.fromEntries(
    Object.entries(sources).map(([source, observation]) => [
      source,
      effectiveWeight(source, observation, now),
    ]),
  );
  const totalWeight = Object.values(weights).reduce((sum, value) => sum + value, 0);

  if (totalWeight <= 0) {
    return buildUncertainState(previous, weights, now);
  }

  const combined = Object.fromEntries(AFFECT_LABELS.map((label) => [label, 0]));
  for (const [source, observation] of Object.entries(sources)) {
    if (!observation || !weights[source]) continue;
    for (const label of AFFECT_LABELS) {
      combined[label] += (
        clamp01(observation.scores?.[label]) * weights[source]
      ) / totalWeight;
    }
  }

  return buildState(
    smoothScores(combined, previous?.scores),
    weights,
    sources,
    previous,
    now,
  );
}

export function affectStateFromApiFusion(
  payload,
  {
    face = null,
    voice = null,
    observedAt = Date.now(),
    turnId = null,
  } = {},
) {
  const scores = normalizeScores(payload?.scores);
  const top = topScore(scores);
  const responseLabel = canonicalLabel(payload?.top_label);
  const label = top.value < 0.55
    ? "uncertain"
    : (responseLabel || top.label);
  const va = valenceArousal(scores);
  return {
    label,
    confidence: top.value,
    scores,
    valence: va.valence,
    arousal: va.arousal,
    trend: "stable",
    policy: ADAPTATION_POLICIES[label] || ADAPTATION_POLICIES.uncertain,
    sources: {
      face: face?.confidence || 0,
      voice: voice?.confidence || 0,
    },
    sourceLabels: {
      face: face?.label || null,
      voice: voice?.label || null,
    },
    sourceWeights: {},
    conflicts: conflictList(face, voice),
    observedAt,
    turnId,
    fusionStrategy: payload?.fusion?.strategy || null,
  };
}

function buildUncertainState(previous, weights, observedAt) {
  const scores = previous?.scores
    ? smoothScores(normalizeScores({ neutral: 1 }), previous.scores, 0.08)
    : normalizeScores({});

  return {
    label: "uncertain",
    confidence: 0,
    scores,
    valence: previous?.valence ? previous.valence * 0.92 : 0,
    arousal: previous?.arousal ? previous.arousal * 0.92 : 0,
    trend: "stable",
    policy: ADAPTATION_POLICIES.uncertain,
    sources: {
      face: 0,
      voice: 0,
    },
    sourceLabels: {
      face: null,
      voice: null,
    },
    sourceWeights: weights,
    conflicts: [],
    observedAt,
  };
}

function buildState(scores, weights, sources, previous, observedAt) {
  const top = topScore(scores);
  const conflicts = conflictList(sources.face, sources.voice);
  const candidateLabel = confidenceLabel(top);
  const label = conflicts.length && CONFIRMATION_REQUIRED_LABELS.has(candidateLabel)
    ? "uncertain"
    : candidateLabel;
  const va = valenceArousal(scores);
  const policy = ADAPTATION_POLICIES[label] || ADAPTATION_POLICIES.uncertain;
  const previousValence = previous?.valence ?? 0;
  const trend = va.valence < previousValence - 0.12
    ? "worsening"
    : va.valence > previousValence + 0.12
      ? "improving"
      : "stable";

  return {
    label,
    confidence: top.value,
    scores,
    valence: va.valence,
    arousal: va.arousal,
    trend,
    policy,
    sources: {
      face: sources.face?.confidence || 0,
      voice: sources.voice?.confidence || 0,
    },
    sourceLabels: {
      face: sources.face?.label || null,
      voice: sources.voice?.label || null,
    },
    sourceWeights: weights,
    conflicts,
    observedAt,
  };
}

export function shouldSendAffectContext(state, lastSent) {
  if (!state || state.label === "uncertain" || state.label === "neutral") return false;
  if (state.confidence < 0.58) return false;
  if (!lastSent) return true;

  const valenceBand = band(state.valence);
  const arousalBand = band(state.arousal);
  return state.label !== lastSent.label
    || valenceBand !== lastSent.valenceBand
    || arousalBand !== lastSent.arousalBand
    || state.policy?.title !== lastSent.policyTitle;
}

export function confirmAdaptation(state, previousCandidate) {
  if (!state || !CONFIRMATION_REQUIRED_LABELS.has(state.label)) {
    return {
      allowed: true,
      nextCandidate: null,
      reason: "confirmation_not_required",
    };
  }

  const faceAgrees = state.sourceLabels?.face === state.label
    && state.sources?.face >= 0.58;
  const voiceAgrees = state.sourceLabels?.voice === state.label
    && state.sources?.voice >= 0.58;
  if (faceAgrees && voiceAgrees) {
    return {
      allowed: true,
      nextCandidate: null,
      reason: "cross_modal_agreement",
    };
  }

  if (previousCandidate?.label === state.label) {
    return {
      allowed: true,
      nextCandidate: null,
      reason: "confirmed_across_turns",
    };
  }

  return {
    allowed: false,
    nextCandidate: {
      label: state.label,
      observedAt: state.observedAt,
    },
    reason: "awaiting_second_turn",
  };
}

function band(value) {
  if (value > 0.25) return "high";
  if (value < -0.25) return "low";
  return "mid";
}

export function affectContextMessage(state) {
  const evidence = [];
  if (state.sources.voice >= 0.45) evidence.push("voice");
  if (state.sources.face >= 0.45) evidence.push("face");
  const conflictText = state.conflicts.length
    ? ` Signal disagreement: ${state.conflicts.join(", ")}.`
    : "";

  return [
    "[AFFECT_CONTEXT]",
    `Observed user affect estimate: ${state.label}, confidence ${state.confidence.toFixed(2)}, ${band(state.arousal)} arousal.`,
    `Evidence: ${evidence.length ? evidence.join("+") : "weak signals"}.${conflictText}`,
    `Adaptation: ${state.policy.instruction}.`,
    "Do not mention emotion detection or claim to know how the user feels.",
  ].join("\n");
}

export function sentContextFingerprint(state) {
  return {
    label: state.label,
    valenceBand: band(state.valence),
    arousalBand: band(state.arousal),
    policyTitle: state.policy?.title || "",
  };
}
